import { FilePlus2, Pencil, ShoppingBag, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SegmentedControl from '../SegmentedControl';
import {
  getProjectToolStartRoute,
  isInteriorShoppingEnabled,
} from '../../lib/projectToolType';
import { projectCanCompare } from '../../lib/projectWorkspaceUtils';
import ProjectDetailsPanel from './ProjectDetailsPanel';

/**
 * @param {{
 *   project: Record<string, unknown>;
 *   resultViewMode: 'new' | 'original' | 'compare';
 *   onResultViewModeChange: (mode: 'new' | 'original' | 'compare') => void;
 *   onEdit?: () => void;
 *   onDelete?: () => void;
 *   onOpenShop?: () => void;
 *   onNewProject?: () => void;
 *   shopItemCount?: number;
 *   showEdit?: boolean;
 * }} props
 */
export default function DesignOutputSidebar({
  project,
  resultViewMode,
  onResultViewModeChange,
  onEdit,
  onDelete,
  onOpenShop,
  onNewProject,
  shopItemCount = 0,
  showEdit = true,
}) {
  const navigate = useNavigate();
  const showShop = isInteriorShoppingEnabled(project) && onOpenShop && project.generatedImageUrl;
  const canCompare = projectCanCompare(project);

  function handleNewProject() {
    onNewProject?.();
    navigate(getProjectToolStartRoute(project), { replace: true });
  }

  function handleViewModeChange(next) {
    onResultViewModeChange(/** @type {'new' | 'original' | 'compare'} */ (next));
  }

  return (
    <div className="project-result-output-body" aria-label="Project output controls">
      <div className="project-result-actions-stack">
        <SegmentedControl
          className="project-result-view-mode-toggle"
          value={resultViewMode}
          onChange={handleViewModeChange}
          ariaLabel="Image view mode"
          options={[
            { value: 'original', label: 'Original', disabled: !project.originalImageUrl },
            { value: 'compare', label: 'Compare', disabled: !canCompare },
            { value: 'new', label: 'New' },
          ]}
        />
        <button
          type="button"
          className="project-result-btn project-result-btn--full project-detail-new-project"
          onClick={handleNewProject}
        >
          <FilePlus2 size={18} strokeWidth={2} aria-hidden />
          New Project
        </button>
        {showEdit && onEdit && project.originalImageUrl ? (
          <button
            type="button"
            className="project-result-btn project-result-btn--full project-detail-edit-project"
            onClick={onEdit}
          >
            <Pencil size={18} strokeWidth={2} aria-hidden />
            Edit
          </button>
        ) : null}
        {showShop ? (
          <button
            type="button"
            className="project-result-btn project-result-btn--shop project-result-btn--full project-detail-shop-furniture"
            onClick={onOpenShop}
          >
            <ShoppingBag size={18} strokeWidth={2} aria-hidden />
            Shop Furniture
          </button>
        ) : null}
        {onDelete ? (
          <button
            type="button"
            className="project-result-btn project-result-btn--full project-detail-delete-project"
            onClick={onDelete}
          >
            <Trash2 size={18} strokeWidth={2} aria-hidden />
            Delete project
          </button>
        ) : null}
      </div>
      <ProjectDetailsPanel project={project} shopItemCount={shopItemCount} />
    </div>
  );
}
